import { Body } from "@sprout/typography";
import useTranslation from "@hooks/use_translation.ts";
import { useMonthOptions } from "@hooks/use_month_options.ts";
import type { ProfileData } from "@/queries/use_profile.ts";
import type { Option } from "@sprout/select";
import type { MonthYear } from "@components/quick_apply/employments";

type Props = { data: ProfileData };

const formatMonthYear = (date: MonthYear | null | undefined, monthOptions: Option[]) => {
  if (!date) return "";

  const month = monthOptions.find((option) => option.value === date.month)?.label ?? "";

  return [month, date.year ? String(date.year) : ""].filter(Boolean).join(" ");
};

const EmploymentBody = ({ data }: Props) => {
  const { t } = useTranslation("user", { keyPrefix: "profile_sections.employment" });
  const months = useMonthOptions();
  const employments = (data.employments ?? []).filter(
    (employment) => employment.company?.trim() || employment.title?.trim(),
  );

  if (employments.length === 0) return null;

  return (
    <ol className="m-0 flex list-none flex-col p-0">
      {employments.map((employment, index) => {
        const range = `${formatMonthYear(employment.startDate, months)} - ${
          formatMonthYear(employment.endDate, months) || t("current")
        }`;
        const subtitle = [employment.company, range].filter(Boolean).join(" • ");
        const isLast = index === employments.length - 1;

        return (
          <li key={employment.key ?? employment.id ?? index} className="flex gap-3">
            <div className="flex flex-col items-center">
              <div className="bg-green-100 flex size-8 shrink-0 items-center justify-center rounded-full">
                <Body as="span" medium>
                  {employment.company?.trim().charAt(0).toUpperCase()}
                </Body>
              </div>
              {!isLast && <div className="border-border w-0 grow border-l" aria-hidden />}
            </div>
            <div className={isLast ? "min-w-0" : "min-w-0 pb-4"}>
              <Body medium>{employment.title}</Body>
              {subtitle && <Body metadata>{subtitle}</Body>}
              {employment.summary && (
                <Body metadata className="mt-2! whitespace-pre-line">
                  {employment.summary}
                </Body>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
};

export default EmploymentBody;
